import type * as ents from '@attain-apps/entities'
import * as ec from '@attain-apps/fe/web/main/lib/entity-collection'
import * as graphql from '@attain-apps/fe/web/main/lib/graphql'
import { TypeAsserter } from '@attain-apps/type-guards'
import * as rxjs from 'rxjs'

import * as common from '../../../common'
import * as conversation from '../conversation'

import * as gql from './gql'
import type * as models from './models'
import type { TypeParams } from './types'

const NON_NULL_AGENT_ID = graphql.transformers.IN.nonNull(
  common.gql.enums.agentId.TRANSFORMER,
)

/**
 * API for managing agent chat messages
 *
 * @see {@link ec.models.Api}
 */
export class Api implements ec.models.Api<TypeParams> {
  /** @inheritdoc */
  public list = ec.models.Api.list.using.nothingButContext<TypeParams>(
    context => {
      if (!context?.conversationId) {
        return rxjs.of([])
      }

      return gql.queries.listMessages.executor({
        conversationId: context.conversationId,
        tenantId: context.tenantId,
        zoneId: context.zoneId,
      })
    },
  )

  /**
   * Emits each of the conversation's messages as it is published.
   *
   * What a message is made of arrives through {@link onInfoEntry}.
   */
  public readonly onMessage = (
    context: TypeParams['context'],
  ): rxjs.Observable<ents.core.agent.Message> => {
    if (!context?.conversationId) {
      return rxjs.EMPTY
    }

    return gql.subscriptions.onMessagePublished.executor({
      conversationId: context.conversationId,
      tenantId: context.tenantId,
      zoneId: context.zoneId,
    })
  }

  /**
   * Emits each info entry published for the conversation's messages.
   *
   * An entry carries which message it belongs to and where in it it sits.
   */
  public readonly onInfoEntry = (
    context: TypeParams['context'],
  ): rxjs.Observable<ents.core.agent.Info.Stored> => {
    if (!context?.conversationId) {
      return rxjs.EMPTY
    }

    return gql.subscriptions.onMessageInfoPublished.executor({
      conversationId: context.conversationId,
      tenantId: context.tenantId,
      zoneId: context.zoneId,
    })
  }

  /** */
  public readonly sendMessage = (
    context: TypeParams['context'],
    entity: models.SendMessageParams.ForRequest,
  ) => {
    TypeAsserter.assertIsNotNullish(context?.tenantId)
    TypeAsserter.assertIsNotNullish(context?.zoneId)

    return gql.mutations.askAgent.executor({
      conversationContext: {
        agentId: NON_NULL_AGENT_ID.getGqlFrom(entity.agentId),
        conversationId: entity.conversationId,
        workspaceId: entity.workspaceId,
      },
      input: {
        id: entity.messageId,
        info: entity.info.map(infoItem => {
          const { createdAt, type, ...payload } = infoItem
          return {
            createdAt: createdAt.toISOString(),
            payload: JSON.stringify(payload),
            type,
          }
        }),
      },
      knowledgeBaseRetrieval: entity.knowledgeBaseRetrieval
        ? {
            records: entity.knowledgeBaseRetrieval.records?.map(record => ({
              modelId: record.modelId,
              recordId: record.recordId,
            })),
          }
        : undefined,
      tenantId: context.tenantId,
      zoneId: context.zoneId,
    })
  }

  /**
   * Retry sending a failed message
   */
  public retryMessage(
    context: TypeParams['context'],
    { messageId }: models.RetryMessageParams,
  ): rxjs.Observable<undefined> {
    TypeAsserter.assertIsNotNullish(context)
    TypeAsserter.assertIsNotNullish(context.conversationId)

    return gql.mutations.retryAgentMessage.executor({
      ...context,
      conversationId: context.conversationId,
      messageId,
    })
  }
}
